import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import get from 'lodash/get';
import defaultTo from 'lodash/defaultTo';
import { useQuery } from '@apollo/client';
import styled from '@emotion/styled';
import { AnalyticsEventSource } from '@atlassian/temp-analytics';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { Box, Flex, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
  FetchTransactionAccountDetailsQuery,
  FetchTransactionAccountDetailsQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import { AccountBalance } from '@css-xp/ppc-account-balance-table';
import { withErrorWrapper } from '@css-xp/ppc-wrapper';
import { PageBackground, PageBackgroundV2 } from '@css-xp/page-background';
import { PPC_PAGE_TITLE, ENABLE_DESIGN_ITERATION_1, routePaths, STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { Background } from '@css-xp/page-background/src/background-image';
import { ScreenEventAnalytics, getCurrencyByCountry, Currency, FormatCurrency } from '@css-xp/ppc-common';
import {
  AppContent,
  AppPage,
  ErrorComponents,
  SummaryPanelContainer,
  SummaryPanelRow,
  SummaryPanelIcon,
  useFeatureToggle,
} from '@css-xp/base';
import { useTitle } from '@css-xp/ppc-custom-hooks';
import { AccountBalancePageLoader } from '@css-xp/ppc-page-loaders';
import { AccountBalanceLabel, AccountBalanceData } from './components';
import { fetchTransactionAccountDetails } from '../queries/fetch-transaction-account-details';
import { CustomerBalances } from '../common/types';
import './account-balance-page.scss';

const ValueWrapper = styled.span`
  font: ${token('font.heading.large')};
  font-weight: ${token('font.weight.medium')};
  padding-top: ${token('space.050')};
  padding-bottom: ${token('space.100')};
`;

const boxStyles = xcss({
  backgroundColor: 'elevation.surface',
  borderColor: 'color.border',
  borderWidth: 'border.width',
  borderStyle: 'solid',
  borderRadius: 'border.radius.200',
  flexGrow: 1,
  height: '80px',
  width: '300px',
});

export const AccountBalancePage = () => {
  const { txaId } = useParams<{ txaId: string }>();
  const [isEnabled] = useFeatureToggle();
  useTitle(PPC_PAGE_TITLE.ACCOUNT_CREDIT_BALANCE, PPC_PAGE_TITLE.BILLING_ACCOUNT);

  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const isMultiCurrencyChangesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES);
  const pageHeading = 'Account credit balance';
  const pageSubHeading =
    "When there's a negative balance on an invoice, the total value is credited back to the billing account and stored as the account credit balance. Any balance automatically applies to the next invoice.";

  const transactionAccountPageResult = useQuery<
    FetchTransactionAccountDetailsQuery,
    FetchTransactionAccountDetailsQueryVariables
  >(fetchTransactionAccountDetails, {
    variables: { id: txaId },
    fetchPolicy: 'no-cache',
    skip: !txaId,
  });
  const { data, loading, error } = transactionAccountPageResult;

  const transactionAccount = data?.transactionAccount;
  const transactionAccountId = transactionAccount?.id ?? txaId;
  const customerBalances = get(data, 'transactionAccount.customerBalances', {});
  const billToAddress = get(data, 'transactionAccount.billToParty', {});

  const initialCurrency = useMemo(() => {
    return isMultiCurrencyChangesEnabled ? getCurrencyByCountry(billToAddress?.country) : Currency.USD;
  }, [billToAddress, isMultiCurrencyChangesEnabled]);

  if (loading) return isDesignIteration1Enabled ? <AccountBalancePageLoader /> : <LoadingIndicator />;
  if (error) {
    const GenericError = withErrorWrapper(ErrorComponents.GenericErrorPage);
    return <GenericError error={error} />;
  }

  const renderCustomerBalanceData = (accountBalances: CustomerBalances) => {
    const currencyWithBalance =
      Object.keys(accountBalances ?? {})?.filter((currencyKey) => typeof accountBalances[currencyKey] === 'number') ??
      [];

    if (currencyWithBalance?.length === 0) {
      // default balance to show if no account balance available
      currencyWithBalance.push('usd');
    }

    const getAccountBalance = (accountBalances: CustomerBalances, currency: string) => {
      return !accountBalances ? 0 : defaultTo(accountBalances[currency], 0);
    };

    return (
      <div className="account-balance-wrapper">
        {currencyWithBalance?.map((currency, index) => (
          <div
            className="account-balance-box"
            key={`currency-${currency}-${index}`}
            data-testid={`currency-${currency}-${index}`}
          >
            <Box padding="space.200" xcss={boxStyles}>
              <Flex direction="column">
                <Text color="color.text.subtlest" size="small">
                  Credit balance
                </Text>
                {isDesignIteration1Enabled ? (
                  <ValueWrapper>
                    <FormatCurrency
                      value={Math.abs(getAccountBalance(accountBalances, currency))}
                      currency={currency?.toUpperCase()}
                      showCurrencyCode
                      showCommas
                    />
                  </ValueWrapper>
                ) : (
                  <span className="account-balance-value">
                    <FormatCurrency
                      value={Math.abs(getAccountBalance(accountBalances, currency))}
                      currency={currency?.toUpperCase()}
                      showCurrencyCode
                      showCommas
                    />
                  </span>
                )}
              </Flex>
            </Box>
          </div>
        ))}
      </div>
    );
  };

  const renderAppPage = () => {
    return (
      <AppPage className={'app-page-v2 txa-account-balance-page'}>
        <ScreenEventAnalytics
          event={{
            name: AnalyticsEventSource.PARTNER_ACCOUNT_BALANCE_SCREEN,
            attributes: {},
          }}
        />
        <AppContent className="txa-balance-page-container">
          {isDesignIteration1Enabled && renderCustomerBalanceData(customerBalances)}
          {!isDesignIteration1Enabled && (
            <SummaryPanelContainer title="Overview" iconKey={SummaryPanelIcon.SUBSCRIPTION_ICON}>
              {isMultiCurrencyChangesEnabled ? (
                renderCustomerBalanceData(customerBalances)
              ) : (
                <SummaryPanelRow
                  label={<AccountBalanceLabel />}
                  value={
                    <div className="account-balance-value">
                      <AccountBalanceData
                        customerBalances={get(data, 'transactionAccount.customerBalances', {}) || {}}
                      />
                    </div>
                  }
                />
              )}
            </SummaryPanelContainer>
          )}
          <div className="txa-balance-table-container">
            <AccountBalance
              label={isDesignIteration1Enabled ? '' : 'Account credits and debits'}
              transactionAccountId={transactionAccountId}
              version={isDesignIteration1Enabled && 4}
              currency={initialCurrency}
              isMultiCurrencyChangesEnabled={isMultiCurrencyChangesEnabled}
            />
          </div>
        </AppContent>
      </AppPage>
    );
  };

  if (isEnabled(ENABLE_DESIGN_ITERATION_1)) {
    const breadcrumbsItems = [
      {
        title: 'Home',
        href: routePaths.home,
      },
      {
        title: 'Billing account',
        href: `${routePaths.details}#details`,
      },
      {
        title: 'Account credit balance',
        href: routePaths.accountBalance,
      },
    ];

    return (
      <PageBackgroundV2
        heading={
          <div className="flex-column">
            {pageHeading}
            <Text color="color.text.subtle">{pageSubHeading}</Text>
          </div>
        }
        breadcrumbsProps={{ items: breadcrumbsItems }}
        className="account-balance-page-v2"
      >
        {renderAppPage()}
      </PageBackgroundV2>
    );
  }

  return (
    <PageBackground
      heading={pageHeading}
      imageKey={Background.ACCOUNT_BALANCE_BACKGROUND}
      className="page-background account-balance-page"
      isBackButtonEnabled={true}
    >
      {renderAppPage()}
    </PageBackground>
  );
};
